// react
import { useEffect } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// api
import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface ErrorWithData {
  data?: {
    message?: string;
    detail?: { ErrorMessage?: string };
  };
}

function hasErrorData(error: unknown): error is ErrorWithData {
  return typeof error === 'object' && error !== null && 'data' in error;
}

function getErrorMessage(error: unknown): string | undefined {
  if (!hasErrorData(error) || !error.data) return undefined;
  return error.data.detail?.ErrorMessage || error.data.message;
}

/**
 * Landing-page (home route) behavior for accounts:
 * - On successful load with at least one account, redirect to the last
 *   selected account (from local storage) or the first available account.
 * - On error, surface the failure via the global alert.
 * Callers use the returned `hasAccounts`/`isLoading` flags to decide whether
 * to render the "get started" call to action instead.
 */
export function useAccountsRedirect() {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  const [cookieAccountId] = useLocalStorage('recalc_account_id', 0);

  const {
    data: accounts,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetAccountsQuery();

  useAlertProvider({
    enabled: isError,
    severity: 'error',
    title: t('home.errors.loadAccountsTitle'),
    message: getErrorMessage(error) || t('home.errors.loadAccountsMessage'),
  });

  useEffect(() => {
    if (!isSuccess || !accounts || !accounts.length) return;

    const targetAccount =
      accounts.find(
        (account: { AccountId: number }) =>
          account.AccountId.toString() === cookieAccountId?.toString(),
      ) || accounts[0];

    navigate({ to: `/${targetAccount.AccountId}`, replace: true });
  }, [isSuccess, accounts, cookieAccountId, navigate]);

  const hasAccounts = isSuccess && Boolean(accounts?.length);

  return { isLoading, hasAccounts };
}
